/**
 * Display names for DocuWare index fields.
 *
 * WHY THIS EXISTS. The expandable row shows the raw payload DocuWare delivers, whose field names
 * are German, abbreviated and inconsistent between the two organisations. Reconciled against both
 * cabinets on 2026-08-20 (131 dch + 287 dbi rows, 134 distinct field names: 55 in both, 35 dch
 * only, 41 dbi only). The reconciliation is in `context/docs/docuware-invoices.md`.
 *
 * THE POINT OF THE MAP. Several fields mean the same thing under different names, and a reader
 * comparing the two companies must not have to know that:
 *
 *     WAEHRUNG / WAHRUNG                        -> Currency        (dbi spells it without the E)
 *     WF_STATUS / WORKFLOW_STATUS               -> Approval Status
 *     WF_INFO / WORKFLOW_INFO                   -> Approval Note
 *     WF_ERROR / WORKFLOW_ERROR                 -> Workflow Error
 *     KLASSIFIZIERUNG / KLASSIFIZIERUNG1        -> Classification
 *     LIEF_BANKKONTO_EMPF / LIEFERANT_BANKKONTO_EMPF -> Payee Bank Account
 *     QR_REFERENZ / QRR_REFERENZ                -> QR Reference
 *
 * DELIBERATELY IN CODE, NOT IN THE DATABASE. The *projection* mapping (which DocuWare field feeds
 * which typed column) lives in `DocuWareConfig.FieldMapJson` because it must be fixable without a
 * deploy. These are UI labels, which is ordinary UI text, and every other label in this app is in
 * code. The risk this accepts is the iKlaro cutover (Oct 2026) renaming fields: an unknown field
 * then falls back to its raw name and still renders, so the page degrades to what it does today
 * rather than losing data. A label is never allowed to hide a value.
 */

import type { ResolvedDocuWareUser } from './docuwareInvoices';

export type DocuWareFieldGroup = 'invoice' | 'workflow' | 'payment' | 'bc' | 'system' | 'other';

export const DOCUWARE_FIELD_GROUP_ORDER: DocuWareFieldGroup[] = [
    'invoice', 'workflow', 'payment', 'bc', 'system', 'other',
];

export const DOCUWARE_FIELD_GROUP_LABELS: Record<DocuWareFieldGroup, string> = {
    invoice: 'Invoice',
    workflow: 'Approval workflow',
    payment: 'Payment details',
    bc: 'Business Central',
    system: 'DocuWare system',
    other: 'Other',
};

interface FieldMeta {
    label: string;
    group: DocuWareFieldGroup;
}

const FIELDS: Record<string, FieldMeta> = {
    // ---- invoice -----------------------------------------------------------------
    MANDANT: { label: 'Legal Entity', group: 'invoice' },
    DOKUMENTTYP: { label: 'Document Type', group: 'invoice' },
    RECHNUNGSART: { label: 'Invoice Category', group: 'invoice' },
    LIEFERANTEN_NR: { label: 'Vendor No.', group: 'invoice' },
    LIEFERANTEN_NAME: { label: 'Vendor', group: 'invoice' },
    LIEFERANTEN_ADRESSE: { label: 'Vendor Address', group: 'invoice' },
    LIEFERANTEN_LAND: { label: 'Vendor Country', group: 'invoice' },
    DOKUMENT_NR: { label: 'Invoice No.', group: 'invoice' },
    DOKUMENT_DATUM: { label: 'Invoice Date', group: 'invoice' },
    FALLIGKEITSDATUM: { label: 'Due Date', group: 'invoice' },
    NETTOBETRAG: { label: 'Net Amount', group: 'invoice' },
    MWST_BETRAG: { label: 'VAT Amount', group: 'invoice' },
    BRUTTOBETRAG: { label: 'Gross Amount', group: 'invoice' },
    BRUTTOBETRAG_TXT: { label: 'Gross Amount (text)', group: 'invoice' },
    WAEHRUNG: { label: 'Currency', group: 'invoice' },
    WAHRUNG: { label: 'Currency', group: 'invoice' },        // dbi spelling
    KLASSIFIZIERUNG: { label: 'Classification', group: 'invoice' },
    KLASSIFIZIERUNG1: { label: 'Classification', group: 'invoice' }, // dbi spelling
    BESTELL_NR: { label: 'Purchase Order No.', group: 'invoice' },
    LIEFERANTEN_BESTELL_NRN: { label: 'Vendor Order Nos.', group: 'invoice' },
    KUNDEN_BESTELL_NRN: { label: 'Customer Order Nos.', group: 'invoice' },
    KUNDEN_NR: { label: 'Customer No.', group: 'invoice' },
    KUNDEN_NAME: { label: 'Customer', group: 'invoice' },
    AUFTRAGSNUMMER: { label: 'Order No.', group: 'invoice' },
    REFERENZ: { label: 'Reference', group: 'invoice' },
    REFERENZ1: { label: 'Reference', group: 'invoice' },
    KONTIERUNG: { label: 'Account Assignment', group: 'invoice' },
    KOSTENART: { label: 'Cost Type', group: 'invoice' },
    ABGRENZUNG: { label: 'Accrual', group: 'invoice' },
    ABGRENZUNGSSTATUS: { label: 'Accrual Status', group: 'invoice' },
    BEMERKUNG: { label: 'Note', group: 'invoice' },
    BEMERKUNG_ABLAGE: { label: 'Filing Note', group: 'invoice' },
    DW_RECHNUNGS_ID: { label: 'DocuWare Invoice ID', group: 'invoice' },
    JAHR: { label: 'Year', group: 'invoice' },
    MONAT: { label: 'Month', group: 'invoice' },
    MWST_ID: { label: 'VAT ID', group: 'invoice' },
    MWST_CODE: { label: 'VAT Code', group: 'invoice' },
    MWST_EINREICHUNGSPFLICHTIG: { label: 'VAT Filing Required', group: 'invoice' },
    MWST_EINREICHUNGSSTATUS: { label: 'VAT Filing Status', group: 'invoice' },
    MWST_EINREICHUNGSDATUM: { label: 'VAT Filing Date', group: 'invoice' },

    // ---- invoice: Dolder Massara (dit), Italian field names -----------------------
    // Third organisation (cabinet "Fattura"), measured 2026-10-12. Only the fields whose meaning
    // is documented in context/docs/docuware-invoices.md are labelled; the rest of the 35 fall
    // back to their raw name, which is the designed behaviour (a label never hides a value).
    TIPO_DOCUMENTO: { label: 'Document Type', group: 'invoice' },
    RAGIONE_SOCIALE: { label: 'Vendor', group: 'invoice' },
    CODICE_FORNITORE: { label: 'Vendor No.', group: 'invoice' },
    NUMERO: { label: 'Invoice No.', group: 'invoice' },
    DATA: { label: 'Invoice Date', group: 'invoice' },
    SCADENZA: { label: 'Due Date', group: 'invoice' },
    IMPONIBILE: { label: 'Net Amount', group: 'invoice' },
    IVA: { label: 'VAT Amount', group: 'invoice' },
    TOTALE: { label: 'Gross Amount', group: 'invoice' },
    FLUSSO: { label: 'Source (PDF / XML)', group: 'invoice' },

    // ---- approval workflow -------------------------------------------------------
    STATUS: { label: 'Registration Status', group: 'workflow' },      // dit: Registrata, Da Registrare, ...
    IN_GESTIONE: { label: 'Handled By', group: 'workflow' },          // dit
    VISIBILITA: { label: 'Visibility', group: 'workflow' },           // dit: Si / No
    DOKUMENTSTATUS: { label: 'Document Status', group: 'workflow' },
    WF_STATUS: { label: 'Approval Status', group: 'workflow' },
    WORKFLOW_STATUS: { label: 'Approval Status', group: 'workflow' },   // dbi spelling
    WF_INFO: { label: 'Approval Note', group: 'workflow' },
    WORKFLOW_INFO: { label: 'Approval Note', group: 'workflow' },       // dbi spelling
    WF_ERROR: { label: 'Workflow Error', group: 'workflow' },
    WORKFLOW_ERROR: { label: 'Workflow Error', group: 'workflow' },     // dbi spelling
    WF_SUBPROCESS: { label: 'Workflow Subprocess', group: 'workflow' },
    WF_AUFGABE_BEI: { label: 'Pending With', group: 'workflow' },
    WF_ERINNERUNG_AM: { label: 'Reminder Due', group: 'workflow' },
    VERARBEITUNGSART: { label: 'Processing Type', group: 'workflow' },
    ANTRAGSTELLER: { label: 'Requested By', group: 'workflow' },
    FREIZUGEBEN_DURCH: { label: 'Approver', group: 'workflow' },
    FREIGABE_DURCH: { label: 'Approved By', group: 'workflow' },
    FREIGEGEBEN_DURCH: { label: 'Approved By', group: 'workflow' },
    FREIGABE_DURCH_TEAM: { label: 'Approving Team', group: 'workflow' },
    FREIGABE_BUHA_DURCH: { label: 'Accounting Approved By', group: 'workflow' },
    FREIGABE_BUHA_AM: { label: 'Accounting Approved On', group: 'workflow' },
    FREIGABE_RECHNPRUEFUNG_DURCH: { label: 'Invoice Check By', group: 'workflow' },
    FREIGABE_RECHNPRUEFUNG_AM: { label: 'Invoice Check On', group: 'workflow' },
    SACHLICHE_PRUEFUNG_DURCH: { label: 'Factual Check By', group: 'workflow' },
    SACHLICHE_PRUEFUNG_AM: { label: 'Factual Check On', group: 'workflow' },
    FINANZIELLE_PRUEFUNG_DURCH: { label: 'Financial Check By', group: 'workflow' },
    FINANZIELLE_PRUEFUNG_AM: { label: 'Financial Check On', group: 'workflow' },
    ABGELEHNT_AM: { label: 'Rejected On', group: 'workflow' },
    STATUS_STAMPROBOT: { label: 'Stamp Robot Status', group: 'workflow' },
    FLG_KONTOAUSWAHL_MITARBEITER: { label: 'Employee Account Choice', group: 'workflow' },
    EMAIL_REMINDER_ART: { label: 'Reminder Type', group: 'workflow' },
    EMAIL_REMINDER_ZEIT: { label: 'Reminder Time', group: 'workflow' },
    EMAIL_REMINDER_ZEIT_DT: { label: 'Reminder Time', group: 'workflow' },

    // ---- payment details ---------------------------------------------------------
    IBAN: { label: 'IBAN', group: 'payment' },
    ZAHLUNGSBEDINGUNG: { label: 'Payment Terms', group: 'payment' },
    ZAHLDATUM: { label: 'Payment Date', group: 'payment' },
    ZAHLLAUF_ID: { label: 'Payment Run ID', group: 'payment' },
    ZAHLLAUF_STATUS: { label: 'Payment Run Status', group: 'payment' },
    LIEF_BANKKONTO_EMPF: { label: 'Payee Bank Account', group: 'payment' },
    LIEFERANT_BANKKONTO_EMPF: { label: 'Payee Bank Account', group: 'payment' }, // dbi spelling
    LIEF_BANKKONTO_EMPF_BC: { label: 'Payee Bank Account (BC)', group: 'payment' },
    LIEFERANT_BANKKONTO: { label: 'Vendor Bank Account', group: 'payment' },
    BANK_NAME: { label: 'Bank', group: 'payment' },
    BANK_NR: { label: 'Bank No.', group: 'payment' },
    QR_CODE: { label: 'QR Code', group: 'payment' },
    QR_TYP: { label: 'QR Type', group: 'payment' },
    QR_IBAN: { label: 'QR IBAN', group: 'payment' },
    QR_BETRAG: { label: 'QR Amount', group: 'payment' },
    QR_WAEHRUNG: { label: 'QR Currency', group: 'payment' },
    QR_REFERENZ: { label: 'QR Reference', group: 'payment' },
    QRR_REFERENZ: { label: 'QR Reference', group: 'payment' },          // dbi spelling
    QR_BESCHREIBUNG: { label: 'QR Description', group: 'payment' },
    ESR_TEILNEHMER: { label: 'ESR Participant', group: 'payment' },
    ESR_REFERENZNR: { label: 'ESR Reference No.', group: 'payment' },
    ESR_CODIERZEILE: { label: 'ESR Coding Line', group: 'payment' },

    // ---- Business Central --------------------------------------------------------
    BELEGNUMMER: { label: 'BC Document No.', group: 'bc' },
    NUMERO_REGISTRAZIONE: { label: 'BC Document No.', group: 'bc' },   // dit: PI-IT... before posting
    BUCHUNGSDATUM: { label: 'Posting Date (planned)', group: 'bc' },
    BC_BUCHUNGSDATUM: { label: 'BC Posting Date', group: 'bc' },
    BC_BUCHUNGSDATUM_TXT: { label: 'BC Posting Date (text)', group: 'bc' },
    BC_IMPORTDATUM: { label: 'BC Import Date', group: 'bc' },
    BC_IMPORTDATUM_ZEIT: { label: 'BC Import Time', group: 'bc' },
    BC_BETRAG: { label: 'BC Amount', group: 'bc' },
    BC_SST_WAEHRUNG: { label: 'BC Currency', group: 'bc' },
    WAEHRUNG_BC: { label: 'BC Currency', group: 'bc' },
    BUCHUNGSTEXT: { label: 'Posting Text', group: 'bc' },

    // ---- DocuWare system ---------------------------------------------------------
    DWDOCID: { label: 'Document ID', group: 'system' },
    DWSTOREDATETIME: { label: 'Archived On', group: 'system' },
    DWSTOREUSER: { label: 'Archived By', group: 'system' },
    DWMODDATETIME: { label: 'Modified On', group: 'system' },
    DWMODUSER: { label: 'Modified By', group: 'system' },
    DWCONTENTMODDATETIME: { label: 'Content Changed On', group: 'system' },
    DWCONTENTMODUSER: { label: 'Content Changed By', group: 'system' },
    DWLASTACCESSDATETIME: { label: 'Last Opened On', group: 'system' },
    DWLASTACCESSUSER: { label: 'Last Opened By', group: 'system' },
    DWEXTENSION: { label: 'File Type', group: 'system' },
    DWDOCSIZE: { label: 'File Size', group: 'system' },
    DWPAGECOUNT: { label: 'Pages', group: 'system' },
    DWTOTALPAGES: { label: 'Pages (total)', group: 'system' },
    DWSECTIONCOUNT: { label: 'Sections', group: 'system' },
    DWNAME: { label: 'Document Name', group: 'system' },
    DWDISK: { label: 'Disk', group: 'system' },
    DWDISKNO: { label: 'Disk No.', group: 'system' },
    ABGELEGT_VON: { label: 'Filed By', group: 'system' },
    EMAIL_ABSENDER: { label: 'E-mail Sender', group: 'system' },
    EMAIL_BETREFF: { label: 'E-mail Subject', group: 'system' },
    DocuWareFulltext: { label: 'Full Text', group: 'system' },
    // Pseudo-field the search dialog adds to every hit; not a cabinet field, so it appears in
    // neither Fields listing. Found only by watching a live row fall through to "Other".
    '@CABINETNAME': { label: 'File Cabinet', group: 'system' },
};

/** Human label for a DocuWare field, falling back to the raw name so nothing is ever hidden. */
export function docuWareFieldLabel(field: string): string {
    return FIELDS[field]?.label ?? field;
}

export function docuWareFieldGroup(field: string): DocuWareFieldGroup {
    return FIELDS[field]?.group ?? 'other';
}

/** True when the field carries no information — '', null, [] and DocuWare's empty table fields. */
export function isEmptyIndexValue(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    const s = String(value).trim();
    return s === '' || s === '[]';
}

export interface GroupedIndexFields {
    group: DocuWareFieldGroup;
    groupLabel: string;
    entries: Array<{
        field: string;
        label: string;
        value: unknown;
        empty: boolean;
        /**
         * The person behind a login blob, when the server could resolve one. The raw `value`
         * stays alongside it — resolving a name must add information, never replace it.
         */
        resolved?: ResolvedDocuWareUser;
    }>;
}

/**
 * Group index fields for display, sorted by label inside each group.
 *
 * `showEmpty=false` hides fields with no value — of the 134 field names across both cabinets, 71
 * were empty in every single row. Hiding is decided PER VALUE, never from a fixed list, so a field
 * that starts being filled appears on its own without a code change.
 */
export function groupIndexFields(
    indexFields: Record<string, unknown>,
    showEmpty: boolean,
    resolvedUsers: Record<string, ResolvedDocuWareUser> = {}
): GroupedIndexFields[] {
    const buckets = new Map<DocuWareFieldGroup, GroupedIndexFields['entries']>();

    for (const [field, value] of Object.entries(indexFields)) {
        const empty = isEmptyIndexValue(value);
        if (empty && !showEmpty) continue;
        const group = docuWareFieldGroup(field);
        if (!buckets.has(group)) buckets.set(group, []);
        buckets.get(group)!.push({
            field, label: docuWareFieldLabel(field), value, empty,
            resolved: resolvedUsers[field],
        });
    }

    return DOCUWARE_FIELD_GROUP_ORDER
        .filter((g) => buckets.has(g))
        .map((g) => ({
            group: g,
            groupLabel: DOCUWARE_FIELD_GROUP_LABELS[g],
            entries: buckets.get(g)!.sort((a, b) => a.label.localeCompare(b.label)),
        }));
}

/** How many of the fields carry no value — drives the "Show all fields" toggle's caption. */
export function countEmptyIndexFields(indexFields: Record<string, unknown>): number {
    return Object.values(indexFields).filter(isEmptyIndexValue).length;
}
